import { gql } from '@apollo/client';
import { IMAGE_VARIANT_FRAGMENT } from './media.fragments';
import { ACTIVITY_FRAGMENT, BASE_ACTIVITY_FRAGMENT } from './activity.fragments';
import { BASE_PARTNER_COMPANY_FRAGMENT } from './partner.fragments';
import { COMPONENT_FRAGMENT, RAW_MATERIAL_FRAGMENT } from './component.fragments';
import { BASE_SITE_FRAGMENT } from './site.fragments';
import { VIDEO_FRAGMENT } from './video.fragments';

export const CHAIN_ACTIVITY_FRAGMENT = gql`
  fragment chainActivityValues on ChainStepActivity {
    id
    incomingLinks {
      id
      chainStepActivitySourceId
    }
    activity {
      ...activityValues
    }
    chainStepId
    pointPosition {
      x
      y
    }
  }
  ${ACTIVITY_FRAGMENT}
`;

export const CHAIN_ACTIVITY_LIST_FRAGMENT = gql`
  fragment chainActivityListValues on ChainStepActivity {
    id
    incomingLinks {
      id
      chainStepActivitySourceId
    }
    activity {
      ...baseActivityValues
      description
      mediaList {
        ... on Video {
          ...videoValues
        }
        ... on ImageVariant {
          ...imageVariant
        }
      }
      component {
        id
        title
        rawMaterials {
          ...rawMaterialValues
        }
      }
      site {
        ...baseSiteValues
      }
      ownedBy {
        ...basePartnerCompanyValues
      }
      subActivities {
        id
        title
      }
    }
    chainStepId
    pointPosition {
      x
      y
    }
  }
  ${IMAGE_VARIANT_FRAGMENT}
  ${VIDEO_FRAGMENT}
  ${RAW_MATERIAL_FRAGMENT}
  ${BASE_ACTIVITY_FRAGMENT}
  ${BASE_PARTNER_COMPANY_FRAGMENT}
  ${BASE_SITE_FRAGMENT}
`;

export const CHAIN_TASK_DETAILS_FRAGMENT = gql`
  fragment chainTasksDetailsValues on ChainTasksDetails {
    tasks {
      id
      title
      done
    }
    completed
    total
  }
`;

export const SUB_CHAIN_FRAGMENT = gql`
  fragment subChainValues on SubChain {
    id
    title
    childChainId
    outputStepActivitiesIds
    outputComponents {
      ownedBy {
        id
        name
      }
    }
    boundingBoxPosition {
      xMax
      xMin
      yMax
      yMin
    }
  }
`;

// This is used for the request list
export const CHAIN_MIN_BASE_FRAGMENT = gql`
  fragment chainMinBaseValues on Chain {
    id
    title
    image {
      ...imageVariant
    }
    chainTasksDetails {
      completed
      total
    }
  }
  ${IMAGE_VARIANT_FRAGMENT}
`;

// This is used for the component chain list without the position and sub chains
export const CHAIN_BASE_FRAGMENT = gql`
  fragment chainBaseValues on Chain {
    ...chainMinBaseValues
    inUse
    mappingInitialised
    productId
    ownedBy {
      ...basePartnerCompanyValues
    }
    involvedPartnerCompanies {
      ...basePartnerCompanyValues
    }

    partnerRequest {
      id
      requestedByUserId
      componentId
      requestTitle
      requestMessage
      requestReplyMessage
      requestStatus
      requestedToCompany {
        ...basePartnerCompanyValues
      }
      requestedFromCompany {
        ...basePartnerCompanyValues
      }
      requestType
    }
  }
  ${BASE_PARTNER_COMPANY_FRAGMENT}
  ${CHAIN_MIN_BASE_FRAGMENT}
`;

export const CHAIN_STEP_FRAGMENT = gql`
  fragment chainStepValues on ChainStep {
    id
    title
    isDeletable
  }
`;

export const CHAIN_VISIBILITY_SETTINGS_FRAGMENT = gql`
  fragment chainVisibilitySettingsValues on ChainVisibilitySettings {
    id
    chainId
    locationVisibility
  }
`;

export const CHAIN_FRAGMENT = gql`
  fragment chainValues on Chain {
    ...chainBaseValues
    outputComponents {
      ...componentValues
    }
  }
  ${CHAIN_BASE_FRAGMENT}
  ${COMPONENT_FRAGMENT}
`;

export const CHAIN_MAPPING_FRAGMENT = gql`
  fragment chainMappingValues on Chain {
    ...chainBaseValues
    chainTasksDetails {
      tasks {
        id
        title
        done
      }
      completed
      total
    }
    subChains {
      ...subChainValues
    }
    chainStepActivities {
      ...chainActivityListValues
    }
    chainSteps {
      ...chainStepValues
    }
    visibilitySettings {
      ...chainVisibilitySettingsValues
    }
  }
  ${CHAIN_BASE_FRAGMENT}
  ${SUB_CHAIN_FRAGMENT}
  ${CHAIN_ACTIVITY_LIST_FRAGMENT}
  ${CHAIN_STEP_FRAGMENT}
  ${CHAIN_VISIBILITY_SETTINGS_FRAGMENT}
`;
